import './styles.css';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { prop } from '@ipfabric/utils/fp/prop';
import { GraphLayoutButtonTooltip } from 'components/Graph/GraphTooltip/GraphLayoutButtonTooltip';
import { type FC, memo, useEffect, useRef } from 'react';
import { useGraphContextSelectors } from '../GraphContext';
import { GraphPathLookupEdgeTooltip } from './GraphPathLookupEdgeTooltip';

const ARROW_SPACING = 9.5;
const DEFAULT_VERTICAL_SPACE = 10;
const ARROW_WIDTH = 18;

interface Props {
  container: HTMLDivElement | null;
}

interface Position {
  x: number;
  y: number;
}

const getXPosition = (
  container: HTMLDivElement,
  xPosition: number,
  width: number,
) => {
  const desiredPosition = xPosition - width / 2;
  if (desiredPosition < 0) {
    return 0;
  }
  if (xPosition + width / 2 > container.offsetWidth) {
    return container.offsetWidth - width;
  }
  return desiredPosition;
};

const getYPosition = (
  yPosition: number,
  inverseYPosition: number,
  tooltipHeight: number,
  spacing: number,
) => {
  const desiredPosition = yPosition - tooltipHeight - spacing;
  if (desiredPosition < 0) {
    return inverseYPosition + spacing;
  }
  return desiredPosition;
};

const getArrowXPosition = (
  container: HTMLDivElement,
  xPosition: number,
): number => {
  if (xPosition < ARROW_SPACING) {
    return ARROW_SPACING;
  }
  if (xPosition > container.offsetWidth - ARROW_SPACING) {
    return container.offsetWidth - ARROW_SPACING;
  }
  return xPosition - ARROW_WIDTH / 2;
};

const BareGraphTooltip: FC<Props> = (props) => {
  const ctxProps = useGraphContextSelectors({
    tooltip: prop('tooltip'),
  });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const topArrowRef = useRef<HTMLDivElement>(null);
  const bottomArrowRef = useRef<HTMLDivElement>(null);

  const toggleArrowVisibility = (
    arrowToShow: HTMLDivElement,
    arrowToHide: HTMLDivElement,
  ) => {
    if (!arrowToHide.classList.contains('hidden')) {
      arrowToHide.classList.add('hidden');
    }
    if (arrowToShow.classList.contains('hidden')) {
      arrowToShow.classList.remove('hidden');
    }
  };

  const updateTooltipPosition = useStableCallback((position: Position) => {
    if (
      ctxProps.tooltip !== null &&
      tooltipRef.current !== null &&
      props.container !== null &&
      topArrowRef.current !== null &&
      bottomArrowRef.current !== null
    ) {
      const tooltipHeight = tooltipRef.current.offsetHeight;
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const yPosition = Math.round(
        getYPosition(
          position.y,
          ctxProps.tooltip.position.inverseY ?? position.y,
          tooltipHeight,
          ctxProps.tooltip.verticalOffset ?? DEFAULT_VERTICAL_SPACE,
        ),
      );
      const xPosition = Math.round(
        getXPosition(props.container, position.x, tooltipWidth),
      );
      const visibleTopArrow = yPosition > position.y;
      const arrowPositionX = Math.round(
        getArrowXPosition(props.container, position.x) - xPosition,
      );

      /**
       * DON'T MOVE INLINE STYLES TO STATE!
       * Due to performance slowdown, tooltip position is set in styles instead of state.
       */
      if (visibleTopArrow) {
        toggleArrowVisibility(topArrowRef.current, bottomArrowRef.current);
        topArrowRef.current.style.transform = `translate(${arrowPositionX}px)`;
      } else {
        toggleArrowVisibility(bottomArrowRef.current, topArrowRef.current);
        bottomArrowRef.current.style.transform = `translate(${arrowPositionX}px)`;
      }

      tooltipRef.current.style.left = `${xPosition}px`;
      tooltipRef.current.style.top = `${yPosition}px`;

      // Show tooltip with delay to avoid visible repositioning
      tooltipRef.current.classList.remove('invisible');
    } else if (
      ctxProps.tooltip === null &&
      !tooltipRef.current?.classList.contains('invisible')
    ) {
      tooltipRef.current?.classList.add('invisible');
    }
  });

  const handleEdgeTooltipMouseMove = useStableCallback((event: MouseEvent) => {
    if (ctxProps.tooltip?.moveWithMouse) {
      updateTooltipPosition({ x: event.offsetX, y: event.offsetY });
    }
  });

  useEffect(() => {
    if (ctxProps.tooltip !== null) {
      updateTooltipPosition(ctxProps.tooltip.position);
    }
  }, [ctxProps.tooltip]);

  useEffect(() => {
    const eventListeners = new ManagedDomEventListeners();
    if (props.container !== null && ctxProps.tooltip !== null) {
      eventListeners.add([
        props.container,
        'mousemove',
        handleEdgeTooltipMouseMove,
      ]);
    }

    return () => {
      eventListeners.remove();
    };
  }, [ctxProps.tooltip, props.container]);

  if (ctxProps.tooltip === null) {
    return null;
  }

  return (
    <div className='graph-tooltip invisible' ref={tooltipRef}>
      <div className='graph-tooltip__arrow-top' ref={topArrowRef} />
      <div className='graph-tooltip__content'>
        {ctxProps.tooltip.payload.type === 'pathLookupPathInspectorEdge' && (
          <GraphPathLookupEdgeTooltip payload={ctxProps.tooltip.payload} />
        )}
        {ctxProps.tooltip.payload.type === 'layoutButton' && (
          <GraphLayoutButtonTooltip payload={ctxProps.tooltip.payload} />
        )}
      </div>
      <div
        className='graph-tooltip__arrow-bottom hidden'
        ref={bottomArrowRef}
      />
    </div>
  );
};

export const GraphTooltip = memo(BareGraphTooltip);
